import { momentLocalizer as getMomentLocalizer } from "react-big-calendar";
import isEqual from "lodash/isEqual";
import moment from "moment-timezone";
import i18n from "~/i18n";
import Angie from "~/utils/angie";
import { momentLocalizationMap } from "~/utils/localization";

export class LocalizationService {
  enabled = false;
  timezone = "UTC";
  dow = 0;
  timezone_autodetect = true;
  locale = "en";
  languages = [];
  format_time = "HH:mm";
  momentLocalizer = getMomentLocalizer(moment);

  constructor() {
    this.timezone = moment.tz.guess();
    this.locale = moment.locale();
    i18n.on("failedLoading", this.onFailedLoading);
  }

  /**
   * Initialize settings
   * @param {Object} [options={timezone: string, dow: number, format_date: string, timezone_autodetect: boolean, language_id: number}] options
   * @param {Object[]} languages
   * @param {boolean} enabled
   * @return { Object | null}
   */
  configure = (options, languages = [], enabled = false) => {
    this.languages = languages;
    this.enabled = enabled;
    return this.update(options);
  };
  onFailedLoading = (lng, ns, msg) => {
    console.error("Failed to load language:" + lng + ". Fallback language will be EN", {
      message: msg,
    });
  };

  getLocale = (language_id = 0) => {
    const language = this.languages.find((lang) => lang.id === language_id);
    let defaultLocale = moment.locale();
    if (language) {
      let [locale] = language.locale.split(".");
      locale = locale.replace("_", "-");
      switch (locale) {
        case "en-US":
          locale = "en";
          break;
      }
      defaultLocale = locale;
    }
    return defaultLocale;
  };

  loadLanguage = () => {
    if (typeof i18n.changeLanguage === "function") {
      i18n.changeLanguage(this.locale);
    }
  };

  getMomentLocale = () => {
    return momentLocalizationMap[this.locale] ?? "en";
  };

  /**
   * Use to update (exp user change language, or user change some timezone etc)
   * @param {Object} options
   * @return {Object}
   */
  update = (options = {}) => {
    if (!this.enabled) {
      return null; //do nothing - disabled
    }
    const previous = this.getResult();

    const { timezone, dow, format_time, timezone_autodetect, language_id } = options;
    if (timezone) {
      this.timezone = timezone;
    }

    if (typeof dow !== "undefined") {
      this.dow = dow;
    }

    if (format_time) {
      this.format_time = Angie.functions.phpToMomentTimeFormat(format_time);
    }

    if (typeof timezone_autodetect !== "undefined") {
      this.timezone = timezone_autodetect ? moment.tz.guess() : this.timezone;
      this.timezone_autodetect = timezone_autodetect;
    }

    if (typeof language_id !== "undefined") {
      this.locale = this.getLocale(language_id);
    }

    if (!isEqual(previous, this.getResult())) {
      let momentArgs = {};
      let momentLocale = this.getMomentLocale();
      // console.log("i will load", momentLocale);
      if (momentLocale === "sr-cyrl") {
        momentArgs = {
          weekdaysShort: "нед_пон_уто_сре_чет_пет_суб".split("_"),
        };
      }
      moment.tz.setDefault(this.timezone);
      moment.updateLocale(momentLocale, {
        ...momentArgs,
        week: {
          dow: this.dow,
        },
        longDateFormat: {
          LT: this.format_time,
        },
      });
      if (previous.locale !== this.locale) {
        this.loadLanguage();
      }
    } else {
      return null;
    }
    return this.getResult();
  };

  getResult = () => {
    return {
      timezone: this.timezone,
      dow: this.dow,
      timezone_autodetect: this.timezone_autodetect,
      format_time: this.format_time,
      locale: this.locale,
      enabled: this.enabled,
    };
  };
}
const localizationService = new LocalizationService();
export default localizationService;
